import { merge, Observable } from 'rxjs';
import { isBefore } from 'date-fns';
import {
  DayOfWeek,
  Frequency,
  Maybe,
  MonthlyRecurrenceConfigDay,
  MonthlyRecurrenceConfigType,
  notMaybe,
  ScheduleConfiguration,
} from '@iss/libs/graphql';
import { FormControl, Validators } from '@angular/forms';
import { DestroyRef } from '@angular/core';

import { ReactiveFormGroup } from '@iss/core/forms';

export enum DailyScheduleType {
  EveryDay = 'EVERY_DAY',
  EveryWeekday = 'EVERY_WEEKDAY',
}

export enum MonthlyScheduleType {
  EveryMonth = 'EVERY_MONTH',
  NthMonth = 'NTH_MONTH',
}

export interface RecurrenceScheduleControls {
  startDate: FormControl<string | undefined>;
  endDate: FormControl<string | undefined>;
  frequency: FormControl<Frequency | undefined>;
  dailyType: FormControl<DailyScheduleType | undefined>;
  dailyRecurrenceCounter: FormControl<number | undefined>;
  weeklyRecurrenceCounter: FormControl<number | undefined>;
  monthlyRecurrenceCounter: FormControl<number | undefined>;
  monthlyRecurrenceDayNumber: FormControl<number | undefined>;
  weeklyRecurrenceDayNames: FormControl<readonly DayOfWeek[]>;
  monthlyType: FormControl<MonthlyScheduleType | undefined>;
  monthlyRecurrenceConfigCounter: FormControl<number | undefined>;
  monthlyRecurrenceConfigType: FormControl<MonthlyRecurrenceConfigType | undefined>;
  monthlyRecurrenceConfigDay: FormControl<MonthlyRecurrenceConfigDay | undefined>;
}

export class RecurrenceScheduleFormGroup extends ReactiveFormGroup<RecurrenceScheduleControls> {
  public static create(
    destroyRef: DestroyRef,
    recurrence?: Maybe<ScheduleConfiguration>,
  ): RecurrenceScheduleFormGroup {
    return new this({
      startDate: new FormControl(recurrence?.startDate, { nonNullable: true, validators: Validators.required }),
      endDate: new FormControl(recurrence?.endDate, { nonNullable: true, validators: Validators.required }),
      frequency: new FormControl(recurrence?.frequency, { nonNullable: true, validators: Validators.required }),
      dailyRecurrenceCounter: new FormControl(recurrence?.frequency === Frequency.Daily
        ? (recurrence.recurrenceCounter || undefined)
        : undefined, {
        nonNullable: true,
        validators: [ Validators.required, Validators.min(1) ],
      }),
      weeklyRecurrenceCounter: new FormControl(recurrence?.frequency === Frequency.Weekly
        ? (recurrence.recurrenceCounter || undefined)
        : undefined, {
        nonNullable: true,
        validators: [ Validators.required, Validators.min(1) ],
      }),
      monthlyRecurrenceCounter: new FormControl(recurrence?.frequency === Frequency.Monthly
        ? (recurrence.recurrenceCounter || undefined)
        : undefined, {
        nonNullable: true,
        validators: [ Validators.required, Validators.min(1) ],
      }),
      dailyType: new FormControl(recurrence?.dailyEveryWeekday ? DailyScheduleType.EveryWeekday : DailyScheduleType.EveryDay, {
        nonNullable: true,
        validators: Validators.required,
      }),
      monthlyType: new FormControl(recurrence?.monthlyRecurrenceConfig ? MonthlyScheduleType.NthMonth : MonthlyScheduleType.EveryMonth, {
        nonNullable: true,
        validators: Validators.required,
      }),
      monthlyRecurrenceDayNumber: new FormControl(recurrence?.monthlyRecurrenceDayNumber || undefined, {
        nonNullable: true,
        validators: [ Validators.required, Validators.min(1), Validators.max(31) ],
      }),
      weeklyRecurrenceDayNames: new FormControl(recurrence?.weeklyRecurrenceDayNames?.filter(notMaybe) || [], {
        nonNullable: true,
        validators: [ Validators.required, Validators.minLength(1) ],
      }),
      monthlyRecurrenceConfigCounter: new FormControl(recurrence?.recurrenceCounter || undefined, {
        nonNullable: true,
        validators: [ Validators.required, Validators.min(1) ],
      }),
      monthlyRecurrenceConfigType: new FormControl(recurrence?.monthlyRecurrenceConfig?.monthlyRecurrenceConfigType, {
        nonNullable: true,
        validators: Validators.required,
      }),
      monthlyRecurrenceConfigDay: new FormControl(recurrence?.monthlyRecurrenceConfig?.monthlyRecurrenceConfigDay, {
        nonNullable: true,
        validators: Validators.required,
      }),
    }, { destroyRef });
  }

  protected woMonitor(): Observable<unknown> {
    return merge(
      this.controls.frequency.valueChanges,
      this.controls.startDate.valueChanges,
      this.controls.dailyType.valueChanges,
      this.controls.monthlyType.valueChanges,
    );
  }

  protected woDoCheck(): void {
    const frequency = this.controls.frequency.value;
    const dailyType = this.controls.dailyType.value;
    const monthlyType = this.controls.monthlyType.value;
    const dailyRecurrenceCounterDisabled = frequency !== Frequency.Daily || dailyType === DailyScheduleType.EveryWeekday;
    const weeklyRecurrenceCounterDisabled = frequency !== Frequency.Weekly;
    const monthlyRecurrenceCounterDisabled = frequency !== Frequency.Monthly || monthlyType === MonthlyScheduleType.NthMonth;
    const monthlyNthMonthEnabled = frequency === Frequency.Monthly && monthlyType === MonthlyScheduleType.NthMonth;
    const startDate = this.controls.startDate.value;
    this
      .woEnableControl(this.controls.dailyRecurrenceCounter, !dailyRecurrenceCounterDisabled)
      .woEnableControl(this.controls.weeklyRecurrenceCounter, !weeklyRecurrenceCounterDisabled)
      .woEnableControl(this.controls.monthlyRecurrenceCounter, !monthlyRecurrenceCounterDisabled)
      .woEnableControl(this.controls.weeklyRecurrenceDayNames, frequency === Frequency.Weekly)
      .woEnableControl(this.controls.monthlyRecurrenceDayNumber, frequency === Frequency.Monthly && monthlyType === MonthlyScheduleType.EveryMonth)
      .woEnableControl(this.controls.monthlyRecurrenceConfigCounter, monthlyNthMonthEnabled)
      .woEnableControl(this.controls.monthlyRecurrenceConfigType, monthlyNthMonthEnabled)
      .woEnableControl(this.controls.monthlyRecurrenceConfigDay, monthlyNthMonthEnabled)
      .woEnableControl(this.controls.endDate, Boolean(startDate))
    ;

    const endDate = this.controls.endDate.value;

    if (startDate && endDate && !isBefore(new Date(startDate), new Date(endDate))) {
      this.woSetValue(this.controls.endDate, undefined);
    }
  }
}
